/**
 * DriverBoard — estado en vivo de cada repartidor/ruta del día. Datos del
 * adapter useActividadData (paquetes del día + posición GPS por socket). Muestra
 * avance (entregadas/total), estado de ruta y GPS. Clic = ir a Rutas en vivo.
 */
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Truck } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { Card } from '../../../../components/ui/Card';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { GpsSignalIndicator } from '../../../../components/ui/GpsSignalIndicator';

const ESTADO_META = {
  en_ruta: { tone: 'info', dot: 'bg-status-info', label: 'En ruta' },
  finalizada: { tone: 'success', dot: 'bg-status-success', label: 'Finalizada' },
  no_iniciada: { tone: 'neutral', dot: 'bg-status-neutral', label: 'Sin iniciar' },
};

function DriverBoard({ rutas, loading, orderBy = 'attention' }) {
  const { t } = useTranslation();

  const rows = useMemo(() => {
    return (rutas || [])
      .map((r) => {
        const total = r.paradas.length;
        const done = r.paradas.filter(
          (p) => p.estado === 'entregada' || p.estado === 'no_entregada',
        ).length;
        return {
          id: r.id,
          name: r.transportistaNombre,
          codigo: r.rutaCodigo,
          estado: r.estado,
          gps: r.device?.gps,
          done,
          total,
          pendientes: total - done, // paquetes aún en ruta (no entregados)
          pct: total > 0 ? (done / total) * 100 : 0,
          initials: r.avatarInitials,
        };
      })
      .sort((a, b) => {
        if (orderBy === 'enRutaDesc') {
          // Mayor cantidad de paquetes en ruta (pendientes por entregar) primero.
          if (b.pendientes !== a.pendientes) return b.pendientes - a.pendientes;
          return b.total - a.total;
        }
        // Default: en ruta primero, luego por menor avance (necesitan atención).
        const order = { en_ruta: 0, no_iniciada: 1, finalizada: 2 };
        if (order[a.estado] !== order[b.estado]) return order[a.estado] - order[b.estado];
        // Desempate: en ruta sin GPS arriba — no se los puede ubicar, es lo que
        // más urge vigilar.
        const aNoGps = a.estado === 'en_ruta' && !a.gps;
        const bNoGps = b.estado === 'en_ruta' && !b.gps;
        if (aNoGps !== bNoGps) return aNoGps ? -1 : 1;
        return a.pct - b.pct;
      });
  }, [rutas, orderBy]);

  return (
    <Card padding="none" className="overflow-hidden flex flex-col">
      <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 grid place-items-center">
            <Truck className="w-4 h-4" strokeWidth={2.2} />
          </span>
          <h3 className="text-sm font-semibold text-fg">{t('Repartidores en operación')}</h3>
        </div>
        <Link
          to="/admin/live-routes"
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
        >
          {t('Ver rutas en vivo')}
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto" style={{ maxHeight: 420 }}>
        {loading ? (
          <ul className="divide-y divide-border">
            {Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="px-5 py-3">
                <Skeleton className="h-4 w-2/3 mb-2" />
                <Skeleton className="h-2 w-full" />
              </li>
            ))}
          </ul>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Truck />}
            title={t('Sin repartidores en ruta')}
            description={t('No hay repartidores con paquetes asignados para hoy.')}
          />
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => {
              const meta = ESTADO_META[r.estado] || ESTADO_META.no_iniciada;
              return (
                <li key={r.id}>
                  {/* La fila se veía apretable (hover) pero no hacía nada y Clarity lo
                      contaba como clic muerto; el encabezado del panel ya promete
                      "Clic = ir a Rutas en vivo". */}
                  <Link
                    to="/admin/live-routes"
                    className="px-5 py-3 flex items-center gap-3 hover:bg-bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                  >
                  <span className="w-9 h-9 rounded-full bg-bg-muted text-fg-muted grid place-items-center text-xs font-semibold shrink-0">
                    {r.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-fg min-w-0 line-clamp-2">{r.name}</span>
                      <span
                        className={cn('w-1.5 h-1.5 rounded-full shrink-0', meta.dot)}
                        title={t(meta.label)}
                        aria-hidden
                      />
                      {/* GPS: verde cuando hay señal; sin señal (warning) solo
                          tiene sentido cuando va en ruta — ahí el "sin GPS" es
                          accionable (no se lo puede ubicar). En sin_iniciar /
                          finalizada no se muestra nada (no se espera GPS). */}
                      {r.gps ? (
                        <GpsSignalIndicator hasSignal size={12} t={t} />
                      ) : r.estado === 'en_ruta' ? (
                        <GpsSignalIndicator hasSignal={false} size={12} t={t} />
                      ) : null}
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-bg-muted overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full transition-all',
                          r.estado === 'finalizada' ? 'bg-status-success' : 'bg-brand-500',
                        )}
                        style={{ width: `${r.pct}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold tabular-nums text-fg leading-none">
                      {r.done}/{r.total}
                    </div>
                    <div className="inline-flex items-center gap-0.5 text-[10px] text-fg-muted mt-1">
                      <MapPin className="w-3 h-3" />
                      {t(meta.label)}
                    </div>
                  </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

// memo: rinde cuando `rutas` mantiene identidad entre ticks (dirty-flag del
// snapshot + comparación del store); sin datos nuevos no hay re-render ni sort.
export default memo(DriverBoard);
